import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { apiClient } from "@/shared/api/client";
import type { NotificationVariableRow } from "@/shared/api/notificationSettings";
import { Pagination } from "@/shared/ui/Pagination";

/**
 * Alias management — platform owner only (#18935).
 *
 * An alias is a spelling, and a global one: `_TEN_KHACH_HANG_` means the patient's name for every
 * tenant. That is why editing lives here and not on a clinic's settings page — a clinic changing it
 * would change how another clinic's templates render, and the failure is silent: the message still
 * sends, it just prints the parameter name where a value belonged.
 *
 * Built-in spellings are shown but not deletable. They are historical facts about templates that
 * exist in the world, and removing one from a screen would make every one of them print a
 * placeholder with no way back short of a release.
 */
interface PlatformVariableRow extends NotificationVariableRow {
  builtInAliases: string[];
  aliasUsage: Record<string, number>;
}

const platformVariablesApi = {
  list: (): Promise<PlatformVariableRow[]> =>
    apiClient.get<PlatformVariableRow[]>("/v1/platform/variables").then((r) => r.data),
  addAlias: (key: string, alias: string, note: string) =>
    apiClient
      .post(`/v1/platform/variables/${encodeURIComponent(key)}/aliases`, { alias, note })
      .then(() => undefined),
  removeAlias: (alias: string) =>
    apiClient
      .delete(`/v1/platform/variables/aliases/${encodeURIComponent(alias)}`)
      .then(() => undefined),
};

export function PlatformVariablesPage() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const [editing, setEditing] = useState<string | null>(null);
  const [alias, setAlias] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const pageSize = 20;

  const { data, isLoading } = useQuery({
    queryKey: ["platform-variables"],
    queryFn: platformVariablesApi.list,
  });
  const rows = useMemo(() => data ?? [], [data]);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["platform-variables"] });

  const add = useMutation({
    mutationFn: (v: { key: string; alias: string; note: string }) =>
      platformVariablesApi.addAlias(v.key, v.alias, v.note),
    onSuccess: () => {
      setEditing(null);
      setAlias("");
      setNote("");
      setError(null);
      invalidate();
    },
    // The platform validates this and its sentence names the actual conflict — an alias already
    // pointing elsewhere, a canonical name used as one. Showing our own generic message instead
    // would throw away the only part that tells the admin what to do differently.
    onError: (e: unknown) => setError(messageOf(e)),
  });

  const remove = useMutation({
    mutationFn: (a: string) => platformVariablesApi.removeAlias(a),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (e: unknown) => setError(messageOf(e)),
  });

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter(
      (r) =>
        r.key.toLowerCase().includes(needle) ||
        r.label.toLowerCase().includes(needle) ||
        r.aliases.some((a) => a.toLowerCase().includes(needle)),
    );
  }, [rows, q]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageRows = filtered.slice(page * pageSize, (page + 1) * pageSize);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Biến & Alias</h1>
        <p className="text-sm text-muted-foreground">
          Alias là <b>cách viết khác</b> của cùng một biến, dùng chung cho mọi phòng khám. Thêm một
          alias ở đây để mẫu Zalo đặt tên tham số lạ chạy được ngay, không cần release.
        </p>
      </div>

      {error ? (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      ) : null}

      <input
        className="h-9 w-72 rounded-md border px-2 text-sm"
        placeholder="Tìm theo tên biến, nhãn hoặc alias"
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setPage(0);
        }}
      />

      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-3 py-2">Canonical</th>
              <th className="px-3 py-2">Nhãn</th>
              <th className="px-3 py-2">Alias</th>
              <th className="px-3 py-2 text-right">Mẫu dùng</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={4} className="px-3 py-6 text-center text-muted-foreground">
                  Đang tải…
                </td>
              </tr>
            ) : null}
            {pageRows.map((r) => {
              const builtIn = new Set(r.builtInAliases ?? []);
              return (
                <tr key={r.key} className="border-t align-top">
                  <td className="whitespace-nowrap px-3 py-2 font-mono text-xs">{r.key}</td>
                  <td className="px-3 py-2">{r.label}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap items-center gap-1">
                      {r.aliases.map((a) => {
                        const uses = r.aliasUsage?.[a] ?? 0;
                        return (
                          <span
                            key={a}
                            className="inline-flex items-center gap-1 rounded border px-1.5 py-0.5 font-mono text-[11px]"
                            title={
                              builtIn.has(a)
                                ? `Tên dựng sẵn · ${uses} mẫu đang dùng`
                                : `Thêm tay · ${uses} mẫu đang dùng`
                            }
                          >
                            {a}
                            {builtIn.has(a) ? (
                              <span className="text-muted-foreground">•</span>
                            ) : null}
                            <button
                              type="button"
                              className="text-destructive"
                              title="Gỡ alias này"
                              onClick={() => {
                                // Gỡ một cách viết đang được dùng KHÔNG báo lỗi lúc gửi: thư vẫn
                                // đi, chỗ đó in ra nguyên chuỗi. Con số này là cảnh báo duy nhất
                                // có được trước khi chuyện đó xảy ra.
                                const warn =
                                  uses > 0
                                    ? `${uses} mẫu đang viết "${a}". Gỡ đi thì những mẫu đó sẽ in ra đúng chuỗi "${a}" cho khách hàng đọc.\n\n`
                                    : "";
                                if (window.confirm(`${warn}Gỡ "${a}"?`)) {
                                  remove.mutate(a);
                                }
                              }}
                            >
                              ×
                            </button>
                          </span>
                        );
                      })}
                      {editing === r.key ? (
                        <span className="flex items-center gap-1">
                          <input
                            autoFocus
                            className="h-7 w-40 rounded border px-1 font-mono text-[11px]"
                            placeholder="_TEN_KH_"
                            value={alias}
                            onChange={(e) => setAlias(e.target.value)}
                          />
                          <input
                            className="h-7 w-44 rounded border px-1 text-[11px]"
                            placeholder="Lý do / mẫu nào cần"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                          />
                          <button
                            type="button"
                            className="h-7 rounded bg-primary px-2 text-[11px] text-primary-foreground"
                            disabled={!alias.trim() || add.isPending}
                            onClick={() => add.mutate({ key: r.key, alias, note })}
                          >
                            Lưu
                          </button>
                          <button
                            type="button"
                            className="h-7 rounded border px-2 text-[11px]"
                            onClick={() => {
                              setEditing(null);
                              setError(null);
                            }}
                          >
                            Huỷ
                          </button>
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="rounded border border-dashed px-1.5 py-0.5 text-[11px] text-muted-foreground"
                          onClick={() => {
                            setEditing(r.key);
                            setAlias("");
                            setNote("");
                            setError(null);
                          }}
                        >
                          + alias
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right text-xs">{r.templateUsage}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Pagination
        page={page}
        totalPages={totalPages}
        totalElements={filtered.length}
        pageSize={pageSize}
        onChange={setPage}
      />

      <p className="max-w-3xl text-xs text-muted-foreground">
        Alias có dấu <b>•</b> là tên dựng sẵn trong hệ thống. Gỡ được, nhưng gỡ một tên đang có mẫu
        dùng thì những mẫu đó sẽ in ra chính chuỗi <code>_X_</code> cho khách hàng đọc — và thư vẫn
        gửi bình thường nên không có gì báo lại. Số mẫu đang dùng hiện khi rê chuột và trong hộp xác
        nhận. Gỡ xong là tên biến mất khỏi danh sách; cần lại thì gõ vào <b>+ alias</b> như một alias
        mới.
      </p>
    </div>
  );
}

function messageOf(e: unknown): string {
  const err = e as { response?: { data?: { detail?: string; message?: string } } };
  return (
    err?.response?.data?.detail ??
    err?.response?.data?.message ??
    "Không thực hiện được. Kiểm tra kết nối tới Addy Message."
  );
}
